import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "@/hooks/use-toast";
import { Eye, EyeOff } from "lucide-react";
import { authApi, LoginData } from "@/services/authApi";
import { Spinner } from "@/components/Spinner";
import { SupplyChainScene } from "@/components/SupplyChainScene";
import { Logo } from "@/components/Logo";

const Login = () => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const loginData: LoginData = { username, password };
      const response = await authApi.login(loginData);

      if (response.success && response.token) {
        localStorage.setItem("authToken", response.token);
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("userEmail", username);

        if (response.user) {
          localStorage.setItem("userName", response.user.name);
          localStorage.setItem("userCompany", response.user.company);
        }

        toast({
          title: "Login Successful",
          description: "Welcome back!",
        });
        navigate("/");
      }
    } catch (error: any) {
      toast({
        title: "Login Failed",
        description: error.message || "Invalid email or password",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen lg:h-screen lg:overflow-hidden flex bg-gradient-to-br from-background via-background to-primary/5 relative overflow-hidden">
      {/* ── Ambient background (behind both columns) ───────────── */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-gradient-to-r from-primary/10 to-accent/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 left-1/3 w-96 h-96 bg-gradient-to-l from-primary/15 to-secondary/10 rounded-full blur-3xl animate-pulse [animation-delay:1s]" />
        <div className="absolute top-1/2 left-1/4 w-48 h-48 bg-gradient-to-t from-accent/10 to-primary/5 rounded-full blur-2xl animate-pulse [animation-delay:2s]" />
        <div className="absolute inset-0 bg-grid-pattern opacity-[0.025] animate-[move-grid_20s_ease-in-out_infinite]" />
      </div>

      {/* ── LEFT: the end-to-end supply-chain workflow ──────────
          Hidden below `lg` — on narrow viewports the sign-in panel
          takes the full width rather than shrinking a 12-node
          diagram into illegibility. */}
      <div className="hidden lg:block flex-1 min-w-0 relative z-10 overflow-hidden">
        <SupplyChainScene />
      </div>

      {/* ── RIGHT: sign-in panel ──────────────────────────────── */}
      <div className="w-full lg:w-[430px] xl:w-[470px] shrink-0 relative z-10 flex items-center justify-center overflow-y-auto p-6 sm:p-10 bg-card/70 backdrop-blur-xl lg:border-l lg:border-border/60 lg:shadow-[-18px_0_50px_-30px_rgba(0,0,0,0.35)]">
        <div className="w-full max-w-[340px]">
          <div className="mb-7 text-center lg:text-left">
            <div className="flex justify-center lg:justify-start mb-3">
              <Logo size="text-3xl" />
            </div>
            <h1 className="text-xl font-semibold tracking-tight text-foreground">
              Welcome back
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Sign in to your planning workspace.
            </p>
          </div>

          <form onSubmit={handleLogin} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="username" className="text-xs font-medium">
                Username
              </Label>
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="h-10 text-sm"
                required
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-10 text-sm pr-10"
                  required
                />
                <button
                  type="button"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute right-0 top-0 h-full px-3 flex items-center text-muted-foreground hover:text-foreground"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              <div className="flex justify-end">
                <Link
                  to="/forgot-password"
                  className="text-xs text-primary hover:underline font-medium"
                >
                  Forgot password?
                </Link>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full h-10 text-sm font-medium"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <Spinner size="sm" />
                  Signing in...
                </div>
              ) : (
                "Sign In"
              )}
            </Button>
          </form>

          {/* Or — small social icons */}
          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center">
              <Separator className="w-full" />
            </div>
            <div className="relative flex justify-center text-[10px] uppercase">
              <span className="bg-card px-3 text-muted-foreground tracking-wider">or</span>
            </div>
          </div>

          <div className="flex justify-center gap-3">
            <button
              type="button"
              aria-label="Continue with Outlook"
              title="Continue with Outlook"
              className="w-10 h-10 rounded-lg border border-border/60 bg-background/50 hover:bg-accent/10 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#0078D4" d="M7.88 12.04q0 .45-.11.87-.1.41-.33.74-.22.33-.58.52-.37.2-.87.2t-.85-.2q-.35-.21-.57-.55-.22-.33-.33-.75-.1-.42-.1-.86 0-.45.1-.87.11-.41.33-.74.22-.33.57-.52.36-.2.85-.2.5 0 .87.2.36.19.58.52.23.33.33.74.11.42.11.87zM24 12v9.38q0 .46-.33.8-.33.32-.8.32H7.13q-.46 0-.8-.33-.32-.33-.32-.8V18H1q-.41 0-.7-.3-.3-.29-.3-.7V7q0-.41.3-.7Q.58 6 1 6h6.5V2.55q0-.44.3-.75.3-.3.75-.3h12.9q.44 0 .75.3.3.3.3.75V10.85l1.24.72h.01q.1.07.18.18.07.12.07.25zM9 3.75V6h2l.13.01.12.04v-2.3zM18 8.25v-3h-3.75v3zm0 4.5v-3h-3.75v3zm0 4.5v-3h-3.75V14l2.21 1.31zm5.25-7.5h-3v3h3zm0 4.5h-3v3h3zM5.98 9.05q-.85 0-1.56.3-.71.3-1.2.85-.49.54-.75 1.3-.25.74-.25 1.63 0 .85.25 1.56.26.71.74 1.22.5.52 1.18.82.69.3 1.55.3.91 0 1.61-.3.7-.32 1.19-.86.49-.55.73-1.28.25-.74.25-1.61 0-.83-.25-1.55-.24-.71-.71-1.24-.47-.52-1.15-.82-.68-.3-1.55-.3zM7.5 21h12.39L12 16.29V17q0 .41-.3.7-.29.3-.7.3H7.5zm15-.13v-7.24l-5.9 3.54Z"/>
              </svg>
            </button>
            <button
              type="button"
              aria-label="Continue with Gmail"
              title="Continue with Gmail"
              className="w-10 h-10 rounded-lg border border-border/60 bg-background/50 hover:bg-accent/10 flex items-center justify-center transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path fill="#4285F4" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                <path fill="#34A853" d="M5.455 20.997V11.73l-2.473-1.855A2.045 2.045 0 0 0 0 11.511v7.855c0 .904.732 1.636 1.636 1.636z"/>
                <path fill="#FBBC04" d="M18.545 20.997V11.73l2.473-1.855A2.045 2.045 0 0 1 24 11.511v7.855c0 .904-.732 1.636-1.636 1.636z"/>
              </svg>
            </button>
          </div>

          <div className="mt-6 text-center text-xs">
            <span className="text-muted-foreground">Don't have an account? </span>
            <Link to="/register" className="text-primary hover:underline font-medium">
              Sign up
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
